import { useEffect } from 'react';

/** 会务/课程咨询共用的微信二维码（白瑞祥） */
const CONSULT_QR_URL =
  'https://api.yuanhe.gzdaone.com/upload/2026/10/17/qr-consult-wechat.jpg';

interface ConsultStep {
  icon: string;
  label: string;
}

interface ConsultQrModalProps {
  open: boolean;
  onClose: () => void;
  label?: string;
  heading?: string;
  subtitle?: string;
  qrHint?: string;
  notice?: string;
  steps?: ConsultStep[];
}

const defaultSteps: ConsultStep[] = [
  { icon: 'ri-qr-scan-2-line', label: '微信扫码' },
  { icon: 'ri-user-add-line', label: '添加顾问' },
  { icon: 'ri-chat-3-line', label: '在线咨询' },
];

export default function ConsultQrModal({
  open,
  onClose,
  label = '会务咨询',
  heading = '扫码添加会务顾问',
  subtitle = '培训报名、课程调整、团体定制等问题，都可随时咨询',
  qrHint = '请使用微信扫一扫，添加专属会务顾问',
  notice = '咨询时间为工作日 9:00 - 18:00，我们将在收到消息后尽快回复',
  steps = defaultSteps,
}: ConsultQrModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 animate-modal-fade bg-foreground-950/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 text-center animate-modal-pop"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        <button
          type="button"
          aria-label="关闭"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="w-1.5 h-4 rounded-full bg-primary-500"></span>
          <span className="text-sm font-medium text-primary-600">{label}</span>
        </div>
        <h3 className="font-heading text-lg font-bold leading-snug text-foreground-900">
          {heading}
        </h3>
        <p className="mt-2 text-sm text-foreground-600">{subtitle}</p>

        <div className="w-52 h-52 mx-auto mt-5 p-3 rounded-lg bg-background-50 border border-background-200">
          <img
            src={CONSULT_QR_URL}
            alt="微信咨询二维码"
            className="w-full h-full object-contain"
          />
        </div>

        <p className="flex items-center justify-center gap-1.5 mt-4 text-sm text-foreground-700">
          <i className="ri-wechat-line text-accent-600"></i>
          {qrHint}
        </p>

        <div className="grid grid-cols-3 gap-2 mt-5">
          {steps.map((step) => (
            <div key={step.label} className="flex flex-col items-center gap-2 rounded-md bg-background-100 py-3 px-1">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
                <i className={step.icon}></i>
              </span>
              <span className="text-xs text-foreground-600 whitespace-nowrap">{step.label}</span>
            </div>
          ))}
        </div>

        <p className="mt-5 text-xs leading-relaxed text-foreground-400">{notice}</p>
      </div>
    </div>
  );
}